import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { toast } from "react-toastify";
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import { BASE_URL } from "../../Api/ApiUrls";
import BankActionPropertyModal from "./BankActionPropertyModal";

const BankActionProperties = () => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRentalType, setSelectedRentalType] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const rentalTypes = [
    'Plots',
    'Residential Building',
    'Commercial Building',
    'Land',
    'Flat',
    'House',
    'Villa'
  ];

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchProperties = async (page, size, searchQuery, rentalType) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      let url = `${BASE_URL}/auction-property/?page=${page}&page_size=${size}`;
      
      if (rentalType) {
        url += `&property_type=${encodeURIComponent(rentalType)}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });
      const data = res.data?.data || [];
      const pagination = res.data?.pagination || {};

      // Format data
      const formatted = data.map((item) => ({
        ...item,
        id: item.bankprop_id,
        user_name: `${item.user_first_name || ""} ${item.user_last_name || ""}`.trim() || "-",
        user_mobile: item.user_mobile_no || "-",
        user_email: item.user_email || "-",
        admin_status: item.admin_status || "Pending",
      }));

      return { data: formatted, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to fetch properties");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setProperties(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, rentalType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchProperties(page, size, searchQuery, rentalType);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, rentalType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchProperties(page, 100, searchQuery, rentalType);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        allProps = [...allProps, ...result.data];
        lastPagination = result.pagination;
        setProperties([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, rentalType) => {
    if (size > 100) {
      loadProgressively(page, size, searchQuery, rentalType);
    } else {
      loadPage(page, size, searchQuery, rentalType);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, selectedRentalType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRentalType, pageSize]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, selectedRentalType);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      // Update local state immediately for better UX
      setProperties((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, admin_status: newStatus } : item
        )
      );

      await axios.put(`${BASE_URL}/auction-property/${id}/`, { admin_status: newStatus });
      toast.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
      // Revert on error
      runLoad(currentPage, pageSize, search, selectedRentalType);
    }
  };

  const handleSearch = (event) => setSearch(event.target.value);

  const handleRentalTypeChange = (event, newValue) => {
    setSelectedRentalType(newValue);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;

    try {
      await axios.delete(`${BASE_URL}/auction-property/${id}/`);
      toast.success("Deleted successfully!");
      runLoad(currentPage, pageSize, search, selectedRentalType);
    } catch (err) {
      console.error("Error deleting:", err);
      toast.error("Failed to delete property. Please try again.");
    }
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, selectedRentalType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleView = (item) => {
    setSelectedRow(item);
    setOpenModal(true);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Bank Auction Properties Table
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            alignItems: "center"
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Search Bank / City / Type"
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearch}
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && (
              <CircularProgress size={20} />
            )}
          </Box>
        </Box>

        {/* Rental Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedRentalType}
            onChange={handleRentalTypeChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Types" value="" />
            {rentalTypes.map((type) => (
              <Tab key={type} label={type} value={type} />
            ))}
          </Tabs>
        </Box>

        {isLoading && properties.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "S.No",
                    "Prop ID",
                    "User Details",
                    "Bank Name",
                    "Property Type",
                    "City / Town",
                    "Area",
                    "Reserve Price",
                    "EMD Amount",
                    "Possession",
                    "Status",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textAlign: "center",
                        fontSize: 15,
                        fontWeight: "bold",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {properties.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ textAlign: "center" }}>
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {`PROP${item.bankprop_id}`}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.user_name}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {item.user_mobile}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {item.user_email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.bank_name || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.property_type || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.city_town || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.area} {item.units}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      ₹{item.reserve_price}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      ₹{item.emd_amount}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.possession_status || "-"}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Select
                        size="small"
                        value={item.admin_status}
                        onChange={(e) =>
                          handleStatusChange(item.id, e.target.value)
                        }
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton color="info" size="small" onClick={() => handleView(item)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {properties.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}

        <BankActionPropertyModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          data={selectedRow}
        />
      </Box>
    </Container>
  );
};

export default BankActionProperties;